// The materialisation pass every course island wears on its way in.
//
// Two coats render on the same geometry, so skinning and animation can never
// drift between them: a projected wire lattice sweeps up the model first, then
// the model's real materials chase the lattice up the same path. Everything the
// two coats must agree on — the sweep frontier, the noise that roughens it, the
// triplanar frame — is defined once in `REVEAL_FIELDS` below, because the moment
// they disagree by a pixel the hand-off tears open.
import * as THREE from 'three';

/** How long one island takes to materialise. */
export const REVEAL_SECONDS = 1.0;

/* ── Timeline (all in reveal progress, 0 → 1) ───────────────────────────── */
// The two sweeps deliberately overlap. Run back to back — lattice finishing at
// 0.43, materials starting at 0.46, as they used to — the middle third of the
// reveal is dead air: the lattice has already closed over everything and the
// materials have not started. Starting the material coat while the lattice is
// still climbing means there are always two frontiers travelling, the material
// chasing the wireframe up the model, which is the whole read of the effect.
const LATTICE_END = 0.50; // lattice frontier reaches the top of the island
const SOLID_START = 0.20; // real materials set off after it
const BAND_FADE_START = 0.50; // lattice frontier glow starts dimming
const BAND_FADE_END = 0.68;
const SOLID_GLOW_FADE_START = 0.9;
// One bright beat as the lattice closes over the top of the model.
const CHARGE_CENTRE = 0.52;
const CHARGE_WIDTH = 0.075;

/* ── Spatial ────────────────────────────────────────────────────────────── */
// Every scale here is picked so `SPACING * scale` lands on a whole number —
// 15 * 2.4, 15 * 0.6, and 15 * (0.6 * 5) for the grain octave below. The
// patterns live in world space and islands are stacked at -index * SPACING, so
// anything else hands each course a different pattern phase: one island's
// lattice lining up with its panels while the next one's cuts across them.
const GRID_SCALE = 2.4;
const NOISE_SCALE = 0.6;
const GRAIN_OCTAVE = 5.0; // frequency multiplier for the fine frontier detail
const GLOW_WIDTH = 0.17; // frontier glow reach, in world units
const GLOW_PIXELS = 14.0; // …and its ceiling on screen, in pixels
// How far below the frontier the lattice still reads as freshly drawn.
const LATTICE_CHARGE_DEPTH = 1.7;
const NOISE_STRENGTH = 0.16; // how far the noise displaces the frontier
// The lattice keeps drawing this far into already-solid surface, fading out,
// rather than being cut away the instant the solid coat passes.
const LATTICE_LINGER = 0.5;
const EDGE_FEATHER = 2.6; // frontier softness, in pixels
const SCAN_FREQUENCY = 26.0;
const SCAN_SPEED = 1.9;

/** GLSL has no implicit int→float, so every interpolated constant needs a
    decimal point of its own. */
const f = (value) => value.toFixed(4);

/* ── Baked noise ─────────────────────────────────────────────────────────
   The frontier used to roughen itself with a sin-based hash — four hashes per
   octave, two octaves, twice over in the lattice coat: sixteen `sin` calls per
   fragment, for noise that still showed the diagonal streaking that hash is
   known for. Baking the octaves into a texture instead buys strictly better
   noise for three samples, and the sample is a triplanar one, which also fixes
   the streaking the old XZ-only lookup left on every vertical surface.     */

const NOISE_SIZE = 128;

const hash2 = (x, y, seed) => {
  let h = Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
};

const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);

const GRADIENTS = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [0.7071, 0.7071], [-0.7071, 0.7071], [0.7071, -0.7071], [-0.7071, -0.7071],
];

/**
 * Gradient (Perlin) noise on a lattice that wraps at `period`.
 *
 * Gradient rather than value noise for one visible reason: interpolating cell
 * *values* leaves every cell a rectangle of roughly one brightness, and once the
 * frontier cuts along those contours the dissolve breaks the model into
 * axis-aligned blocks. Interpolating cell *slopes* gives contours that curve, so
 * the same cut crumbles. Hashing the lattice modulo the period is what lets the
 * finished tile repeat without a seam.
 */
const tileNoise = (u, v, period, seed) => {
  const x = u * period;
  const y = v * period;
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const fx = x - xi;
  const fy = y - yi;
  const sx = fade(fx);
  const sy = fade(fy);

  const at = (i, j) => {
    const cell = hash2((xi + i) % period, (yi + j) % period, seed);
    const gradient = GRADIENTS[Math.floor(cell * 8) & 7];
    return gradient[0] * (fx - i) + gradient[1] * (fy - j);
  };

  const top = at(0, 0) + (at(1, 0) - at(0, 0)) * sx;
  const bottom = at(0, 1) + (at(1, 1) - at(0, 1)) * sx;
  return top + (bottom - top) * sy;
};

const tileFbm = (u, v, basePeriod, octaves, seed) => {
  let sum = 0;
  let total = 0;
  let amplitude = 1;
  let period = basePeriod;
  for (let octave = 0; octave < octaves; octave += 1) {
    sum += tileNoise(u, v, period, seed + octave * 17) * amplitude;
    total += amplitude;
    amplitude *= 0.5;
    period *= 2;
  }
  return sum / total;
};

/**
 * Three decorrelated fBm fields in one RGBA tile: R carries the broad shape of
 * the frontier, G the mid detail, B the fine crumble at its lip. One fetch
 * returns all three, so the multi-octave field costs a single sample per plane.
 */
const buildRevealNoiseTexture = () => {
  const data = new Uint8Array(NOISE_SIZE * NOISE_SIZE * 4);
  const channels = [
    { basePeriod: 4, octaves: 4, seed: 11 },
    { basePeriod: 8, octaves: 3, seed: 907 },
    { basePeriod: 24, octaves: 2, seed: 5231 },
  ];

  channels.forEach(({ basePeriod, octaves, seed }, channel) => {
    const values = new Float32Array(NOISE_SIZE * NOISE_SIZE);
    let low = Infinity;
    let high = -Infinity;

    for (let y = 0; y < NOISE_SIZE; y += 1) {
      for (let x = 0; x < NOISE_SIZE; x += 1) {
        const value = tileFbm(x / NOISE_SIZE, y / NOISE_SIZE, basePeriod, octaves, seed);
        values[y * NOISE_SIZE + x] = value;
        if (value < low) low = value;
        if (value > high) high = value;
      }
    }

    // Gradient noise is signed and its octave average clusters hard around the
    // middle. Stretching each channel back over the full range keeps the
    // frontier's displacement at the amplitude the constants above ask for.
    const span = Math.max(high - low, 1e-5);
    for (let i = 0; i < values.length; i += 1) {
      data[i * 4 + channel] = Math.round(((values[i] - low) / span) * 255);
    }
  });

  for (let i = 0; i < NOISE_SIZE * NOISE_SIZE; i += 1) data[i * 4 + 3] = 255;

  const texture = new THREE.DataTexture(data, NOISE_SIZE, NOISE_SIZE, THREE.RGBAFormat);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  // Displacement data, not colour: an sRGB decode here would skew the frontier.
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
};

let revealNoiseTexture = null;

/**
 * The one noise tile the whole world shares.
 *
 * Cached at module scope rather than per mount, and that is not just tidiness:
 * the materialiser lives inside the islands' Suspense boundary, so every time a
 * sibling suspends on an asset the subtree re-renders from scratch and every
 * useMemo body runs again. Baking it there ran this six times over a page load —
 * about 160ms of blocked main thread for six identical 64KB textures. It is the
 * same field for every island and every mount, so it is built once and kept.
 */
export const getRevealNoiseTexture = () => {
  if (!revealNoiseTexture) revealNoiseTexture = buildRevealNoiseTexture();
  return revealNoiseTexture;
};

/* ── Shared GLSL ────────────────────────────────────────────────────────── */

const VERTEX_PARS = `#include <common>
    varying vec3 vRevealWorldPosition;`;

const VERTEX_WORLD_POSITION = `#include <skinning_vertex>
    vRevealWorldPosition = (modelMatrix * vec4(transformed, 1.0)).xyz;`;

const REVEAL_PARS = `#include <common>
    uniform float uRevealProgress;
    uniform float uRevealTop;
    uniform float uRevealBottom;
    uniform float uRevealTime;
    uniform float uRevealDarken;
    uniform vec3 uRevealGlowColor;
    uniform sampler2D uRevealNoise;
    varying vec3 vRevealWorldPosition;

    // Quintic smootherstep. Both its first and second derivatives vanish at the
    // ends, so the sweep accelerates and settles without the visible kink in
    // speed a cubic leaves behind.
    float revealEase(float value) {
      float t = clamp(value, 0.0, 1.0);
      return t * t * t * (t * (t * 6.0 - 15.0) + 10.0);
    }

    // Frontier travel, deliberately closer to linear than the quintic above.
    // Smootherstep's tails are so flat that the last sixth of the sweep barely
    // moves — and that is exactly the stretch where the model is already fully
    // revealed, so the animation reads as having stalled before it ends.
    float revealSweep(float value) {
      float t = clamp(value, 0.0, 1.0);
      return mix(t, t * t * (3.0 - 2.0 * t), 0.55);
    }

    // Interleaved gradient noise (Jimenez, 2014) as the per-pixel threshold for
    // the opaque coat's cut-off. A low-discrepancy sequence stipples the
    // boundary evenly; white noise clumps into speckle you can see.
    float revealDither() {
      return fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
    }

    // How much of this pixel sits below the frontier, measured in pixels rather
    // than world units. Normalising by the frontier's own screen derivative is
    // what turns a binary edge into a sub-pixel gradient, and it holds at any
    // camera distance or surface angle — the single biggest reason the old
    // boundary read as stepped.
    float revealCoverage(float belowEdge, float feather) {
      return clamp(0.5 + belowEdge / max(fwidth(belowEdge) * feather, 1e-5), 0.0, 1.0);
    }

    // Frontier glow, measured two ways at once, tighter of the two winning.
    //
    // A world-space width on its own floods any surface lying flat across the
    // sweep: a platform top sits at a single height, so every pixel of it is
    // "at the frontier" in the same instant and the whole disc turns into one
    // white blob. Screen-space distance — the offset divided by its own
    // derivative — is near-infinite on exactly those surfaces, so it holds the
    // glow to a line of constant apparent thickness wherever an edge is really
    // visible, and suppresses it where none is.
    //
    // Two falloffs: a tight core for the line itself and a squared halo around
    // it, because one smoothstep reads as a flat painted stripe rather than
    // something emitting light.
    float revealBand(float belowEdge, float width, float pixels) {
      float distanceToEdge = abs(belowEdge);
      float t = max(
        distanceToEdge / width,
        distanceToEdge / max(fwidth(belowEdge) * pixels, 1e-5)
      );
      float core = 1.0 - smoothstep(0.0, 0.34, t);
      float halo = 1.0 - smoothstep(0.0, 1.0, t);
      return core * 0.55 + halo * halo * 0.35;
    }`;

// Everything both coats share. Declared at the top of main(), so the tail
// blocks further down the shader can still read all of it.
const REVEAL_FIELDS = `
    // Faceted world normal, straight from the world position's screen
    // derivatives. Deriving it here rather than carrying a varying is
    // deliberate: both coats then build a bit-identical frame without depending
    // on which normal chunks a given GLTF material happens to compile in, and
    // faceted is exactly what triplanar weights want anyway.
    vec3 revealPosition = vRevealWorldPosition;
    vec3 revealFacet = cross(dFdx(revealPosition), dFdy(revealPosition));
    float revealFacetArea = length(revealFacet);

    // Never hand a zero vector to normalize. A triangle seen exactly edge on,
    // or one that collapses to no screen area, makes that cross product zero,
    // and normalize(0) is NaN — which does not stay local: the NaN reaches
    // gl_FragColor, the bloom pass blurs it across its mipmap chain, and the
    // whole frame composites to black, background included. +Y is the right
    // fallback because it is the axis the sweep travels along.
    vec3 revealNormal = revealFacetArea > 1e-12
      ? revealFacet / revealFacetArea
      : vec3(0.0, 1.0, 0.0);

    vec3 revealAxis = pow(abs(revealNormal), vec3(4.0));
    revealAxis /= max(revealAxis.x + revealAxis.y + revealAxis.z, 1e-4);

    // Drifting the lookup with progress keeps the frontier churning as it
    // climbs instead of sliding up as one rigid pattern.
    vec3 revealNoiseUv = revealPosition * ${f(NOISE_SCALE)} + uRevealProgress * 0.3;
    vec3 revealField =
        texture2D(uRevealNoise, revealNoiseUv.zy).rgb * revealAxis.x
      + texture2D(uRevealNoise, revealNoiseUv.xz).rgb * revealAxis.y
      + texture2D(uRevealNoise, revealNoiseUv.xy).rgb * revealAxis.z;
    float revealNoise = revealField.r * 0.58 + revealField.g * 0.28 + revealField.b * 0.14;

    // A high-frequency octave rides on top, and it is doing something specific:
    // its steep gradient breaks the cut into small crumbling patches. The coarse
    // octaves alone leave a surface lying nearly flat across the sweep — a
    // platform top, a shallow dome — in partial coverage all at once, and the
    // dither renders that wide zone as a visible screen door. Steep noise gives
    // the frontier somewhere to be sharp.
    vec3 revealGrainUv = revealPosition * ${f(NOISE_SCALE * GRAIN_OCTAVE)} - uRevealProgress * 0.45;
    float revealGrain =
        texture2D(uRevealNoise, revealGrainUv.zy).r * revealAxis.x
      + texture2D(uRevealNoise, revealGrainUv.xz).r * revealAxis.y
      + texture2D(uRevealNoise, revealGrainUv.xy).r * revealAxis.z;

    float revealNoiseOffset = (
      (revealNoise - 0.5) * 0.74 + (revealGrain - 0.5) * 0.26
    ) * ${f(NOISE_STRENGTH)};

    // Both frontiers ride the same displaced height, so the lattice's leading
    // edge and the solid coat's trailing edge trace the same silhouette.
    float revealLatticeEdge = mix(
      uRevealBottom, uRevealTop,
      revealSweep(uRevealProgress / ${f(LATTICE_END)})
    ) + revealNoiseOffset;
    float revealSolidEdge = mix(
      uRevealBottom, uRevealTop,
      revealSweep((uRevealProgress - ${f(SOLID_START)}) / ${f(1 - SOLID_START)})
    ) + revealNoiseOffset;

    float revealBelowLattice = revealLatticeEdge - revealPosition.y;
    float revealBelowSolid = revealSolidEdge - revealPosition.y;`;

/* ── Solid coat ─────────────────────────────────────────────────────────── */

const SOLID_CUT = `#include <clipping_planes_fragment>

    // Everything below runs only while the island is still building.
    //
    // uRevealProgress is a uniform, so this branch is coherent across the whole
    // draw — no divergence, and the derivatives inside it stay well defined. A
    // finished island is what the visitor spends nearly all their time looking
    // at, and without this it would keep paying for six texture fetches, two
    // screen derivatives and the whole noise field on every fragment of every
    // frame to compute a frontier that has already run off the top.
    float revealSolidBand = 0.0;
    if (uRevealProgress < 1.0) {
    ${REVEAL_FIELDS}

    // Cut here, at the top of main(), not down at the end: every fragment above
    // the frontier now skips the whole PBR lighting evaluation.
    //
    // The cut is forced fully open as the sweep lands, and that guard is not
    // decoration. The frontier's ceiling comes from measuring the island, and a
    // measurement that reads low does not merely mistime the reveal — it leaves
    // whatever sits above it discarded for as long as the course is on screen.
    // A model missing the top of its head is a far worse failure than a sweep
    // that finishes a frame early, so completion wins over the measurement.
    float revealSettled = smoothstep(0.94, 1.0, uRevealProgress);
    float revealCut = max(
      revealCoverage(revealBelowSolid, ${f(EDGE_FEATHER)}),
      revealSettled
    );
    if (revealCut < revealDither()) discard;

    revealSolidBand = revealBand(revealBelowSolid, ${f(GLOW_WIDTH)}, ${f(GLOW_PIXELS)})
      * (0.78 + revealNoise * 0.34)
      * (1.0 - revealEase(
          (uRevealProgress - ${f(SOLID_GLOW_FADE_START)}) / ${f(1 - SOLID_GLOW_FADE_START)}
        ));
    }`;

const SOLID_GLOW = `
    // At night the frontier emits into the dark scene. In daylight an additive
    // glow can only get brighter, so it could never produce the requested dark
    // transition. Blend the same band toward its dark ink colour instead.
    vec3 revealBrightFrontier = gl_FragColor.rgb + uRevealGlowColor * revealSolidBand;
    vec3 revealDarkFrontier = mix(
      gl_FragColor.rgb,
      uRevealGlowColor,
      clamp(revealSolidBand * 0.82, 0.0, 1.0)
    );
    gl_FragColor.rgb = mix(revealBrightFrontier, revealDarkFrontier, uRevealDarken);
    #include <dithering_fragment>`;

/* ── Lattice coat ───────────────────────────────────────────────────────── */

const LATTICE_PARS = `${REVEAL_PARS}

    uniform vec3 uRevealLineColor;

    // One anti-aliased grid plane. Normalising the distance-to-line by the cell
    // coordinate's screen derivative holds every line at the same apparent
    // width, near or far, head-on or edge-on. The second term drops the lines
    // out once a cell shrinks below a pixel — without it, distant and grazing
    // surfaces collapse into solid glowing blobs, which is the aliasing the
    // old grid was most visibly guilty of.
    float revealGridPlane(vec2 cell) {
      vec2 cellWidth = max(fwidth(cell), vec2(1e-4));
      vec2 toLine = abs(fract(cell - 0.5) - 0.5) / cellWidth;
      float line = min(toLine.x, toLine.y);
      float density = max(cellWidth.x, cellWidth.y);
      return (1.0 - smoothstep(0.62, 1.5, line)) * (1.0 - smoothstep(0.34, 1.1, density));
    }`;

const LATTICE_CUT = `#include <clipping_planes_fragment>
    ${REVEAL_FIELDS}

    // Nothing exists above the lattice frontier yet, and past the linger
    // distance the solid coat has long since taken over. Both are broad
    // regions, so culling them before any lattice shading is nearly free.
    if (
      revealBelowLattice < ${f(-GLOW_WIDTH * 2.2)}
      || revealBelowSolid > ${f(LATTICE_LINGER)}
    ) discard;

    float revealLatticeAlpha = revealCoverage(revealBelowLattice, ${f(EDGE_FEATHER)});

    // The lattice does not vanish the instant the solid coat passes. It lingers
    // over the fresh surface and dims out, which is what covers the seam the
    // old hard swap between coats left behind.
    revealLatticeAlpha *= 1.0 - smoothstep(
      0.0, ${f(LATTICE_LINGER)}, max(revealBelowSolid, 0.0)
    );

    // Triplanar grid, each plane weighted by how much the surface faces it.
    // That weighting is the fix for the old single min() across all three axes:
    // on a face lying flat against one of the grid planes that axis' term was
    // near zero everywhere, so whole panels lit up as blotches instead of
    // drawing lines.
    vec3 revealCell = revealPosition * ${f(GRID_SCALE)};
    float revealLine =
        revealGridPlane(revealCell.zy) * revealAxis.x
      + revealGridPlane(revealCell.xz) * revealAxis.y
      + revealGridPlane(revealCell.xy) * revealAxis.z;

    float revealLatticeBand = revealBand(revealBelowLattice, ${f(GLOW_WIDTH)}, ${f(GLOW_PIXELS)})
      * (0.78 + revealNoise * 0.34)
      * (1.0 - revealEase(
          (uRevealProgress - ${f(BAND_FADE_START)}) / ${f(BAND_FADE_END - BAND_FADE_START)}
        ));

    // Grazing angles read brighter, the way a projected volume does.
    vec3 revealView = normalize(cameraPosition - revealPosition);
    float revealRim = pow(1.0 - abs(dot(revealNormal, revealView)), 3.0);

    // Scanlines climb faster than the sweep does, and a shallow flicker keeps
    // the projection from looking like a static cage.
    float revealScan = sin(revealPosition.y * ${f(SCAN_FREQUENCY)} - uRevealTime * ${f(SCAN_SPEED)});
    revealScan = 0.66 + 0.34 * revealScan * revealScan;
    float revealFlicker = 0.95 + 0.05 * sin(uRevealTime * 23.0 + revealPosition.y * 4.0);

    float revealCharge = exp(
      -pow((uRevealProgress - ${f(CHARGE_CENTRE)}) / ${f(CHARGE_WIDTH)}, 2.0)
    );

    // Lines just drawn sit brighter than lines the frontier left behind a while
    // ago. Without this the lattice is one flat cage; with it the wireframe
    // reads as being drawn, which is most of what sells a projection.
    float revealFresh = 1.0 - smoothstep(0.0, ${f(LATTICE_CHARGE_DEPTH)}, max(revealBelowLattice, 0.0));`;

const LATTICE_INK = `
    float revealGlow = clamp(revealLatticeBand, 0.0, 1.0);
    gl_FragColor.rgb = mix(
      uRevealLineColor,
      uRevealGlowColor,
      clamp(revealGlow * 0.9 + revealFresh * 0.35, 0.0, 1.0)
    );
    gl_FragColor.rgb += uRevealGlowColor * (revealRim * 0.28 + revealCharge * 0.35);

    // Let the frontier bridge the gaps between lines, so the leading edge reads
    // as one continuous cut that then resolves into the lattice behind it.
    float revealInk = max(
      revealLine * revealScan * revealFlicker * (0.7 + revealFresh * 0.3 + revealRim * 0.45),
      revealGlow * 0.6
    ) * (1.0 + revealCharge * 0.6);

    gl_FragColor.a *= revealLatticeAlpha * clamp(revealInk, 0.0, 1.0);
    if (gl_FragColor.a < 0.004) discard;
    #include <dithering_fragment>`;

/* ── Public API ─────────────────────────────────────────────────────────── */

const PROGRAM_KEY = 'gogamedev-course-reveal-v14';

/** True for the plain GLTF materials this effect knows how to wrap. */
export const isRevealSurface = (material) => material
  && material.isMaterial
  && !material.isShaderMaterial
  && !material.isRawShaderMaterial;

/**
 * The uniform holders for one island. Both coats reference these same objects,
 * so advancing the whole reveal is one number written per island per frame.
 */
const NIGHT_LINE_TINT = new THREE.Color('#D9FBFF');
const NIGHT_GLOW_TINT = new THREE.Color('#F1FEFF');
const DAY_REVEAL_INK = new THREE.Color('#071525');

/** Mutate the shared colour uniforms without rebuilding or recompiling coats. */
export const setRevealPalette = (state, accent, light = false) => {
  const base = new THREE.Color(accent);

  if (light) {
    // The light-theme course accents have dedicated contrast-safe ink values.
    // Pull them a little toward the site's navy so the lattice and frontier
    // read as a dark construction pass against the bright sky.
    state.line.value.copy(base).lerp(DAY_REVEAL_INK, 0.34).multiplyScalar(0.9);
    state.glow.value.copy(base).lerp(DAY_REVEAL_INK, 0.2).multiplyScalar(1.02);
    state.darken.value = 1;
    return;
  }

  state.line.value.copy(base).lerp(NIGHT_LINE_TINT, 0.12).multiplyScalar(0.48);
  // Deliberately past 1: these values have to cross the night scene's bloom
  // threshold, while the lattice lines themselves stay crisp.
  state.glow.value.copy(base).lerp(NIGHT_GLOW_TINT, 0.16).multiplyScalar(1.5);
  state.darken.value = 0;
};

export const createRevealState = ({ accent, top, bottom, revealed, time, noise }) => {
  const state = {
    progress: { value: revealed ? 1 : 0 },
    line: { value: new THREE.Color() },
    glow: { value: new THREE.Color() },
    darken: { value: 0 },
    top: { value: top },
    bottom: { value: bottom },
    time,
    noise,
  };
  setRevealPalette(state, accent, false);
  return state;
};

const bindShared = (shader, state) => {
  shader.uniforms.uRevealProgress = state.progress;
  shader.uniforms.uRevealTop = state.top;
  shader.uniforms.uRevealBottom = state.bottom;
  shader.uniforms.uRevealGlowColor = state.glow;
  shader.uniforms.uRevealDarken = state.darken;
  shader.uniforms.uRevealTime = state.time;
  shader.uniforms.uRevealNoise = state.noise;
};

/** A clone of a model's own material that reveals along the shared frontier. */
/**
 * Teach a material to reveal along the frontier, in place.
 *
 * Returns the undo, because this is applied to materials the rest of the app
 * still holds references to and mutates.
 */
export const applySolidReveal = (material, state) => {
  const previousCompile = material.onBeforeCompile;
  const previousKey = material.customProgramCacheKey;
  const previousExtensions = material.extensions;
  const sourceKey = previousKey.call(material);

  // The injected reveal code uses dFdx/dFdy/fwidth. They are core in WebGL2,
  // but WebGL1 phones need OES_standard_derivatives enabled explicitly. Most
  // PBR materials happened to request it for their own normal mapping, masking
  // the omission until a simpler model material was opened on an older phone.
  material.extensions = { ...(previousExtensions || {}), derivatives: true };

  material.onBeforeCompile = (shader, renderer) => {
    previousCompile.call(material, shader, renderer);
    bindShared(shader, state);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', VERTEX_PARS)
      .replace('#include <skinning_vertex>', VERTEX_WORLD_POSITION);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', REVEAL_PARS)
      .replace('#include <clipping_planes_fragment>', SOLID_CUT)
      .replace('#include <dithering_fragment>', SOLID_GLOW);
  };

  material.customProgramCacheKey = () => `${PROGRAM_KEY}|solid|${sourceKey}`;
  material.needsUpdate = true;

  return () => {
    material.onBeforeCompile = previousCompile;
    material.customProgramCacheKey = previousKey;
    if (previousExtensions) material.extensions = previousExtensions;
    else delete material.extensions;
    material.needsUpdate = true;
  };
};

/** A private copy of a shared material, revealing along the frontier. */
export const makeSolidMaterial = (source, state) => {
  const material = source.clone();
  applySolidReveal(material, state);
  return material;
};

/** The wire lattice coat, drawn from the same geometry as a second group. */
export const makeLatticeMaterial = (state) => {
  const material = new THREE.MeshBasicMaterial({
    color: state.line.value,
    transparent: true,
    opacity: 0.92,
    // Keeps the lattice self-occluding, so you read the front of the model
    // rather than through it.
    depthWrite: true,
    // The lattice now overlaps the solid coat for LATTICE_LINGER world units,
    // and both coats come from one geometry at exactly the same depth. Nudging
    // the lattice toward the camera is what stops that overlap z-fighting.
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    toneMapped: false,
  });

  // See applySolidReveal: the lattice is MeshBasicMaterial, so unlike a
  // physical material it never enables derivatives on its own in WebGL1.
  material.extensions = { derivatives: true };

  material.onBeforeCompile = (shader) => {
    bindShared(shader, state);
    shader.uniforms.uRevealLineColor = state.line;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `${VERTEX_PARS}
    uniform float uRevealProgress;`
      )
      .replace('#include <skinning_vertex>', VERTEX_WORLD_POSITION)
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>
    // Once an island is solid the lattice has nothing left to draw, but its
    // groups keep getting submitted for as long as the course is on screen.
    // Collapsing the vertex outside the clip volume costs one compare, where
    // rasterising those fragments to discard them costs a full pass over the
    // model every frame.
    if (uRevealProgress >= 1.0) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);`
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', LATTICE_PARS)
      .replace('#include <clipping_planes_fragment>', LATTICE_CUT)
      .replace('#include <dithering_fragment>', LATTICE_INK);
  };

  material.customProgramCacheKey = () => `${PROGRAM_KEY}|lattice`;
  material.needsUpdate = true;
  return material;
};
